import { AgentType, ModelProvider } from '@rematter/frontend-lib-api-client';
import { Getter, atom } from 'jotai';

import { agentCreateMutationAtom } from '../_graphql/mutationAgentCreate';
import { agentSetToolsMutationAtom } from '../_graphql/mutationAgentSetTools';
import { agentUpdateMutationAtom } from '../_graphql/mutationAgentUpdate';
import { agentQueryAtom } from '../_graphql/queryAgent';
import { adminRoutes } from '../routes';

import type { QueryAtomResult } from '~/shared/providers/api/createQueryAtomFactory';
import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

export type AgentFormData = {
  name: string;
  content: string;
  description?: string;
  type: AgentType;
  agentIdentifier?: string;
  organizationId?: string;
  toolIds: string[];
  isActive: boolean;
  modelProvider?: ModelProvider;
  model?: string;
  providerApiKey?: string;
  providerBaseUrl?: string;
  reasoningEnabled?: boolean;
  reasoningBudget?: number;
  temperature?: number;
};

// Route params for edit view
export const $agentDetailPathParams = atomFromRoutePath(
  adminRoutes.aiAgentDetail,
);
export const $agentId = atom((get) => get($agentDetailPathParams)?.id ?? null);

// Detect if we are on the "new" route
export const $isNewRoute = atomFromRoutePath(adminRoutes.aiAgentNew);
export const $isCreateMode = atom((get) =>
  Boolean(get($isNewRoute)?.__didMatch),
);

// Optional query param to preselect type on creation
export const $newAgentType = atomFromQueryString.string<'Agent' | 'SubAgent'>(
  'type',
  'SubAgent',
);

// Remote agent (edit mode only)
export const $remoteAgent = agentQueryAtom((get: Getter) => ({
  variables: { id: get($agentId) ?? '' },
  skip: !get($agentId) || get($isCreateMode),
}));

const getInitialFormData = (type: AgentType): AgentFormData => ({
  name: '',
  content: '',
  description: undefined,
  type,
  agentIdentifier: type === 'SubAgent' ? '' : undefined,
  organizationId: undefined,
  toolIds: [],
  isActive: false,
  // AI Model Configuration - defaults
  modelProvider: 'Gemini' as ModelProvider,
  model: 'gemini-2.5-flash',
  providerApiKey: undefined,
  providerBaseUrl: undefined,
  reasoningEnabled: false,
  reasoningBudget: 1024,
  temperature: 0.7,
});

// Helper to map remote agent data to form data
type AgentRecord = QueryAtomResult<typeof agentQueryAtom>;
const mapRemoteAgentToForm = (agent: AgentRecord): AgentFormData => ({
  name: agent.name ?? '',
  content: agent.content ?? '',
  description: agent.description ?? undefined,
  type: (agent.type as AgentType) ?? 'SubAgent',
  agentIdentifier: agent.agentIdentifier ?? undefined,
  organizationId: agent.organizationId ?? undefined,
  toolIds: agent.tools ?? [],
  isActive: agent.isActive ?? false,
  modelProvider:
    (agent.modelProvider as ModelProvider) ?? ('Gemini' as ModelProvider),
  model: agent.model ?? 'gemini-2.5-flash',
  providerApiKey: agent.providerApiKey ?? undefined,
  providerBaseUrl: agent.providerBaseUrl ?? undefined,
  reasoningEnabled: agent.reasoningEnabled ?? false,
  reasoningBudget: agent.reasoningBudget ?? 1024,
  temperature: agent.temperature ?? 0.7,
});

// Base form data derived from remote agent or defaults
const $baseFormData = atom<AgentFormData>((get) => {
  const isCreate = get($isCreateMode);

  if (isCreate) {
    const type = (get($newAgentType) as AgentType) ?? 'SubAgent';
    return getInitialFormData(type);
  }

  const remoteAgent = get($remoteAgent);
  if (remoteAgent.data) {
    return mapRemoteAgentToForm(remoteAgent.data);
  }

  return getInitialFormData('SubAgent');
});

// Track which agent we're currently editing
const $lastSyncedAgentId = atom<string | null>(null);
const $lastIsCreateMode = atom<boolean | null>(null);

// Writable form atom with auto-sync on agent change
const $agentDetailFormData = atom<AgentFormData>(
  getInitialFormData('SubAgent'),
);

// Combined read/write atom that auto-syncs when agent changes
export const $agentDetailForm = atom(
  (get) => {
    const currentAgentId = get($agentId);
    const lastSynced = get($lastSyncedAgentId);
    const isCreate = get($isCreateMode);
    const lastIsCreate = get($lastIsCreateMode);
    const baseData = get($baseFormData);

    // If route mode or agent changed, return base data (will trigger sync in setter)
    if (currentAgentId !== lastSynced || isCreate !== lastIsCreate) {
      return baseData;
    }

    return get($agentDetailFormData);
  },
  (get, set, update: Partial<AgentFormData>) => {
    const currentAgentId = get($agentId);
    const lastSynced = get($lastSyncedAgentId);
    const isCreate = get($isCreateMode);
    const lastIsCreate = get($lastIsCreateMode);
    const baseData = get($baseFormData);

    // If route mode or agent changed, sync to base first
    if (currentAgentId !== lastSynced || isCreate !== lastIsCreate) {
      set($lastSyncedAgentId, currentAgentId);
      set($lastIsCreateMode, isCreate);
      set($agentDetailFormData, baseData);
    }

    // Apply the update
    const current = get($agentDetailFormData);
    set($agentDetailFormData, { ...current, ...update });
  },
);

export const $updateAgentDetailForm = atom(
  null,
  (get, set, update: Partial<AgentFormData>) => {
    set($agentDetailForm, update);
  },
);

// UI State - Active tab for Agent Detail view
export type AgentDetailsTab = 'configuration' | 'prompt' | 'tools';
export const $tab = atomFromQueryString.string<AgentDetailsTab>('tab');

// Mutations
export const $createAgent = agentCreateMutationAtom({});
export const $updateAgent = agentUpdateMutationAtom({});
export const $setAgentTools = agentSetToolsMutationAtom({});

// Save action (create or update)
export const $saveAgentDetail = atom(
  null,
  async (get, set): Promise<string | null> => {
    const isCreate = get($isCreateMode);
    const id = get($agentId);
    const form = get($agentDetailForm);

    if (isCreate) {
      const result = await set($createAgent, {
        name: form.name,
        content: form.content,
        description: form.description,
        type: form.type,
        agentIdentifier: form.agentIdentifier,
        organizationId: form.organizationId,
        modelProvider: form.modelProvider,
        model: form.model,
        providerApiKey: form.providerApiKey,
        providerBaseUrl: form.providerBaseUrl,
        reasoningEnabled: form.reasoningEnabled,
        reasoningBudget: form.reasoningBudget,
        temperature: form.temperature,
      });
      const newId = result?.assistantCreateAgent?.id ?? null;
      if (newId && form.toolIds?.length) {
        await set($setAgentTools, { id: newId, toolIds: form.toolIds });
      }
      return newId;
    }

    if (id) {
      await set($updateAgent, {
        id,
        name: form.name,
        content: form.content,
        description: form.description,
        type: form.type,
        agentIdentifier: form.agentIdentifier,
        organizationId: form.organizationId,
        modelProvider: form.modelProvider,
        model: form.model,
        providerApiKey: form.providerApiKey,
        providerBaseUrl: form.providerBaseUrl,
        reasoningEnabled: form.reasoningEnabled,
        reasoningBudget: form.reasoningBudget,
        temperature: form.temperature,
      });
      if (form.toolIds) {
        await set($setAgentTools, { id, toolIds: form.toolIds });
      }
      return id;
    }

    return null;
  },
);
